import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { PrivilegeService } from '@core/services/privilege.service';
import { CoreTranslatingService } from '@core/services/core-translating.service';
import { TranslateService } from '@ngx-translate/core';
import { DeviceCmd, Devices, DeviceAlarm } from '@api';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  isDeviceCard?: boolean;
  deviceSummary?: DeviceSummary;
  isStatusMessage?: boolean;
  isBillingLimit?: boolean;
  isAuthError?: boolean;
}

interface DeviceSummary {
  mac: string;
  model: string;
  firmware: string;
  ip: string;
  status: string;
  network: string;
  band: string;
  rsrp: number | null;
  sinr: number | null;
}

type WorkflowType = 'health' | 'connectivity' | 'alerts' | null;

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer: ElementRef;
  @ViewChild('messageInput') messageInput: ElementRef;

  isOpen = false;
  isVisible = false;
  isExpanded = false;
  messages: ChatMessage[] = [];
  inputText = '';
  isLoading = false;
  isFetchingDevice = false;
  providerName = '';
  currentDeviceContext = '';
  currentDeviceMAC = '';
  currentDeviceSummary: DeviceSummary | null = null;
  currentSyslog = '';
  currentSyslogSummary = '';
  lastAIDiagnostic = '';
  pendingWorkflow: WorkflowType = null;
  statusMessage = '';
  statusStep = 0;
  usedFallbackData = false;
  loadingStatusText = '';
  billingLimitMessage: string | null = null;
  lowBalanceWarning: string | null = null;
  readonly LOW_BALANCE_THRESHOLD = 5.00;

  private destroy$ = new Subject<void>();
  private macPattern = /^[0-9A-Fa-f]{12}$/;
  private currentAbortController: AbortController | null = null;
  private loadingStatusTimers: any[] = [];

  get isDeviceOnline(): boolean {
    return this.currentDeviceSummary?.status === 'Online';
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private privilegeService: PrivilegeService,
    private coreTranslating: CoreTranslatingService,
    private _translate: TranslateService,
    private deviceCmd: DeviceCmd,
    private devicesService: Devices,
    private deviceAlarmService: DeviceAlarm
  ) {}

  ngOnInit() {
    this.checkVisibility();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkVisibility();
      });

    this.privilegeService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkVisibility();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }
    this.stopLoadingMessages();
  }

  private checkVisibility() {
    const isOnPagesRoute = this.router.url.startsWith('/pages');
    const isLoggedIn = !!this.privilegeService.currentUserValue;
    const shouldBeVisible = isOnPagesRoute && isLoggedIn;

    if (shouldBeVisible && !this.isVisible) {
      this.isVisible = true;
      this.loadConfig();
    } else if (!shouldBeVisible) {
      this.isVisible = false;
      this.isOpen = false;
    }
    this.cdr.detectChanges();
  }

  async loadConfig() {
    try {
      const res = await fetch('/api/chat/config');
      const data = await res.json();
      this.providerName = data.provider || 'AI Assistant';
      this.cdr.detectChanges();
    } catch {
      this.providerName = 'AI Assistant';
    }
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.checkBalance();
      setTimeout(() => {
        this.messageInput?.nativeElement?.focus();
        this.scrollToBottom();
      }, 100);
    }
  }

  private async checkBalance() {
    try {
      const res = await fetch('/api/billing/balance');
      if (!res.ok) return;
      const data = await res.json();
      if (!data.ok) return;
      const balance: number | null = data.balanceUsd ?? null;
      if (balance !== null && balance <= this.LOW_BALANCE_THRESHOLD) {
        if (balance <= 0) {
          this.lowBalanceWarning = `Your Copilot credit balance is $0.00. Please add credits to continue using the assistant.`;
        } else {
          this.lowBalanceWarning = `Low credit balance: $${balance.toFixed(2)} remaining. Top up soon to avoid interruption.`;
        }
      } else {
        this.lowBalanceWarning = null;
      }
      this.cdr.detectChanges();
    } catch {}
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  selectWorkflow(workflow: WorkflowType) {
    this.pendingWorkflow = workflow;
    const labels = {
      health: this._translate.instant('CHATBOT.WORKFLOW_HEALTH'),
      connectivity: this._translate.instant('CHATBOT.WORKFLOW_CONNECTIVITY'),
      alerts: this._translate.instant('CHATBOT.WORKFLOW_ALERTS')
    };
    this.messages.push({
      role: 'user',
      content: labels[workflow]
    });
    this.messages.push({
      role: 'assistant',
      content: this._translate.instant('CHATBOT.WORKFLOW_DEVICE_PROMPT')
    });
    this.cdr.detectChanges();
    this.scrollToBottom();
    setTimeout(() => this.messageInput?.nativeElement?.focus(), 100);
  }

  private extractMAC(text: string): string | null {
    const cleaned = text.replace(/[:\-\s.]/g, '').toUpperCase();
    if (this.macPattern.test(cleaned)) {
      return cleaned;
    }
    const match = cleaned.match(/\b([0-9A-F]{12})\b/);
    return match ? match[1] : null;
  }

  private extractMACFromText(text: string): string | null {
    const labeled = text.match(/\bMAC[:\s]+([0-9A-Fa-f]{2}[:\-]){5}[0-9A-Fa-f]{2}/i);
    if (labeled) {
      return labeled[0].replace(/.*MAC[:\s]+/i, '').replace(/[:\-]/g, '').toUpperCase();
    }
    const colonMAC = text.match(/\b([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}\b/);
    if (colonMAC) return colonMAC[0].replace(/:/g, '').toUpperCase();
    const solidMAC = text.match(/\b([0-9A-Fa-f]{12})\b/);
    if (solidMAC) return solidMAC[1].toUpperCase();
    return null;
  }

  private extractAllMACsFromText(text: string): string[] {
    const found = new Set<string>();
    const labeledRe = /\bMAC[:\s]+([0-9A-Fa-f]{2}[:\-]){5}[0-9A-Fa-f]{2}/gi;
    let m: RegExpExecArray | null;
    while ((m = labeledRe.exec(text)) !== null) {
      found.add(m[0].replace(/.*MAC[:\s]+/i, '').replace(/[:\-]/g, '').toUpperCase());
    }
    const colonRe = /\b([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}\b/g;
    while ((m = colonRe.exec(text)) !== null) {
      found.add(m[0].replace(/:/g, '').toUpperCase());
    }
    const solidRe = /\b([0-9A-Fa-f]{12})\b/g;
    while ((m = solidRe.exec(text)) !== null) {
      found.add(m[1].toUpperCase());
    }
    return Array.from(found);
  }

  private extractDeviceNameFromText(text: string): string | null {
    const patterns = [
      /\bdevice\s+named?\s+"?([^",\n]+?)"?\s*(?:\.|,|$)/i,
      /\bnamed?\s+"([^"]+)"/i,
      /\bfor\s+(?:device\s+)?"?([A-Za-z0-9][\w\s\-\.]{1,40})"?\s*(?:\.|,|\?|$)/i,
      /\banalyze\s+(?:the\s+)?(?:device\s+)?(?:name\s+)?"?([A-Za-z0-9][\w\s\-\.]{1,40})"?/i,
      /\bcheck\s+(?:the\s+)?(?:device\s+)?(?:name\s+)?"?([A-Za-z0-9][\w\s\-\.]{1,40})"?/i,
      /\bdevice\s+"([^"]+)"/i,
    ];
    for (const p of patterns) {
      const m = text.match(p);
      if (m) return m[1].trim();
    }
    return null;
  }

  private async fetchDeviceInfoByName(name: string): Promise<void> {
    try {
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const filter = {
        where: {
          or: [
            { name: { regexp: `/${escapedName}/i` } },
            { subscriberName: { regexp: `/${escapedName}/i` } }
          ]
        },
        limit: 1
      };
      const resp: any = await this.devicesService.getDevices(filter).pipe(take(1)).toPromise();
      let device: any = null;
      if (resp?.data?.length > 0) device = resp.data[0];
      else if (Array.isArray(resp) && resp.length > 0) device = resp[0];
      if (device?.MAC) {
        await this.fetchDeviceInfo(device.MAC.replace(/[:\-]/g, '').toUpperCase());
      }
    } catch {
      // Silent — if name lookup fails, no context is set
    }
  }

  async sendMessage() {
    const text = this.inputText.trim();
    if (!text || this.isLoading || this.isFetchingDevice) return;

    this.messages.push({ role: 'user', content: text });
    this.inputText = '';
    this.scrollToBottom();

    if (this.pendingWorkflow) {
      const mac = this.extractMAC(text);
      if (mac) {
        await this.executeWorkflow(mac);
        return;
      }
      const workflowPrompts = {
        health: `Analyze device health for device named "${text}". Get the device info and provide a full signal quality and health analysis.`,
        connectivity: `Troubleshoot connectivity for device named "${text}". Get the device info, run a ping test to google.com, and provide a combined connectivity analysis.`,
        alerts: `Investigate alerts for device named "${text}". Look up the device and check for any recent issues.`
      };
      const workflow = this.pendingWorkflow;
      this.pendingWorkflow = null;
      this.messages[this.messages.length - 1] = {
        role: 'user',
        content: workflowPrompts[workflow]
      };
      await this.sendToAI(true);
      return;
    }

    if (!this.currentDeviceContext) {
      const mac = this.extractMAC(text);
      if (mac) {
        await this.executeWorkflow(mac);
        return;
      }
      await this.sendToAI(true);
      return;
    }

    await this.sendToAI(true);
  }

  private async executeWorkflow(mac: string) {
    const workflow = this.pendingWorkflow || 'health';
    this.pendingWorkflow = null;

    switch (workflow) {
      case 'health':
        await this.runHealthWorkflow(mac);
        break;
      case 'connectivity':
        await this.runConnectivityWorkflow(mac);
        break;
      case 'alerts':
        await this.runAlertsWorkflow(mac);
        break;
    }
  }

  private async runHealthWorkflow(mac: string) {
    const success = await this.fetchDeviceInfo(mac);
    if (!success) return;

    this.setStatus(3, this._translate.instant('CHATBOT.STATUS_ANALYZING_HEALTH'));
    this.messages.push({ role: 'assistant', content: '' });
    this.isLoading = true;
    this.cdr.detectChanges();
    await this.streamAIAnalysis();
    this.clearStatus();
  }

  private async runConnectivityWorkflow(mac: string) {
    const success = await this.fetchDeviceInfo(mac);
    if (!success) return;

    let pingResults = '';
    if (this.isDeviceOnline) {
      pingResults = await this.fetchPingResults(mac) || '';
    }

    if (this.isDeviceOnline) {
      await this.fetchAndAnalyzeSyslog(true, pingResults);
    } else {
      this.setStatus(3, this._translate.instant('CHATBOT.STATUS_ANALYZING_CONNECTIVITY'));
      this.messages.push({ role: 'assistant', content: '' });
      this.isLoading = true;
      this.cdr.detectChanges();

      const aiMessages = [
        {
          role: 'user',
          content: `I need help troubleshooting connectivity for this device. The device is currently OFFLINE so the system log and ping test are unavailable. Please analyze the last known telemetry data and provide connectivity troubleshooting recommendations.\n\n${this.currentDeviceContext}`
        }
      ];

      const controller = new AbortController();
      this.currentAbortController = controller;

      let isTimedOut = false;
      const timeoutHandle = setTimeout(() => { isTimedOut = true; controller.abort(); }, 90000);

      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ messages: aiMessages }),
          signal: controller.signal
        });

        if (response.status === 401 || response.status === 402) { if (await this._handleBillingLimit(response)) return; }
        if (!response.ok) throw new Error('Failed to get AI response');

        this.clearStatus();
        const assistantMessage = this.messages[this.messages.length - 1];
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          assistantMessage.content += decoder.decode(value, { stream: true });
          this.cdr.detectChanges();
          this.scrollToBottom();
        }

        if (assistantMessage.content && assistantMessage.content.length > 50) {
          this.lastAIDiagnostic = assistantMessage.content;
        }
      } catch (err) {
        if (err?.name === 'AbortError') {
          const lastMsg = this.messages[this.messages.length - 1];
          if (lastMsg && !lastMsg.content) lastMsg.content = isTimedOut ? this._translate.instant('CHATBOT.ERROR_TIMED_OUT') : this._translate.instant('CHATBOT.ERROR_RESPONSE_STOPPED');
        } else {
          const lastMsg = this.messages[this.messages.length - 1];
          if (!lastMsg?.content) lastMsg.content = this._translate.instant('CHATBOT.ERROR_CONNECTIVITY_FAILED');
        }
        this.cdr.detectChanges();
      } finally {
        clearTimeout(timeoutHandle);
      }

      this.currentAbortController = null;
      this.isLoading = false;
      this.clearStatus();
      this.cdr.detectChanges();
      this.scrollToBottom();
    }
  }

  private async runAlertsWorkflow(mac: string) {
    const success = await this.fetchDeviceInfo(mac);
    if (!success) return;

    await this.fetchDeviceAlerts(mac);
  }

  private setStatus(step: number, message: string) {
    this.statusStep = step;
    this.statusMessage = message;
    this.cdr.detectChanges();
  }

  private clearStatus() {
    this.statusStep = 0;
    this.statusMessage = '';
    this.cdr.detectChanges();
  }

  private async fetchDeviceInfo(mac: string): Promise<boolean> {
    this.isFetchingDevice = true;
    this.usedFallbackData = false;
    this.setStatus(1, this._translate.instant('CHATBOT.STATUS_LOCATING_DEVICE', { mac }));

    let data: any = null;

    try {
      const response = await this.deviceCmd.sendDeviceCommand({
        MAC: mac,
        command: 'GET_DEVICE_INFO' as any
      }).pipe(take(1)).toPromise();

      if (response?.isSuccessful && response?.data) {
        data = response.data;
      }
    } catch {
    }

    if (!data) {
      this.setStatus(1, this._translate.instant('CHATBOT.STATUS_DEVICE_OFFLINE_FALLBACK'));
      data = await this.fetchFallbackDeviceData(mac);
      if (!data) {
        this.clearStatus();
        this.messages.push({
          role: 'assistant',
          content: this._translate.instant('CHATBOT.DEVICE_NOT_FOUND', { mac })
        });
        this.isFetchingDevice = false;
        this.cdr.detectChanges();
        return false;
      }
      this.usedFallbackData = true;
    }

    this.setStatus(2, this._translate.instant('CHATBOT.STATUS_FETCHING_TELEMETRY'));
    this.currentDeviceMAC = mac;
    this.currentDeviceContext = this.formatDeviceData(data);
    this.currentSyslog = '';
    this.currentSyslogSummary = '';
    this.lastAIDiagnostic = '';

    const validSignal = (v: any) => typeof v === 'number' && v !== -1000 && isFinite(v);

    const summary: DeviceSummary = {
      mac: data.MAC || mac,
      model: data.MODEL || 'Unknown',
      firmware: data.FW || 'Unknown',
      ip: data.IP || 'N/A',
      status: this.usedFallbackData ? (data.connected ? 'Online' : 'Offline') : (data.Get_Sync === 'Yes' ? 'Online' : 'Offline'),
      network: data.NETMODE || 'Unknown',
      band: data.BAND || 'N/A',
      rsrp: validSignal(data.RSRP) ? data.RSRP : null,
      sinr: validSignal(data.SINR) ? data.SINR : null
    };

    this.currentDeviceSummary = summary;
    this.clearStatus();

    if (this.usedFallbackData) {
      this.messages.push({
        role: 'assistant',
        content: this._translate.instant('CHATBOT.DEVICE_OFFLINE'),
        isStatusMessage: true
      });
    }

    this.messages.push({
      role: 'assistant',
      content: '',
      isDeviceCard: true,
      deviceSummary: summary
    });

    this.isFetchingDevice = false;
    this.cdr.detectChanges();
    this.scrollToBottom();

    return true;
  }

  private async fetchPingResults(mac: string): Promise<string | null> {
    this.setStatus(2, this._translate.instant('CHATBOT.STATUS_RUNNING_PING'));
    this.cdr.detectChanges();

    try {
      const response = await this.deviceCmd.sendDeviceCommand({
        MAC: mac,
        command: 'PING_URL' as any,
        data: { pingUrl: 'google.com' }
      } as any).pipe(take(1)).toPromise();

      if (response?.isSuccessful && response?.data) {
        const data = response.data;
        const parts: string[] = ['PING TEST RESULTS (target: google.com):'];
        if (data.transmitted != null) parts.push(`- Packets Sent: ${data.transmitted}`);
        if (data.received != null) parts.push(`- Packets Received: ${data.received}`);
        if (data.packetLoss != null) parts.push(`- Packet Loss: ${data.packetLoss}%`);
        if (data.min != null) parts.push(`- Min Latency: ${data.min} ms`);
        if (data.avg != null) parts.push(`- Avg Latency: ${data.avg} ms`);
        if (data.max != null) parts.push(`- Max Latency: ${data.max} ms`);
        if (parts.length === 1) {
          parts.push(`- Raw: ${JSON.stringify(data).substring(0, 500)}`);
        }
        return parts.join('\n');
      }
      return 'PING TEST: Could not retrieve results (device may not support ping or is unreachable).';
    } catch {
      return 'PING TEST: Failed to execute ping test.';
    }
  }

  private async fetchFallbackDeviceData(mac: string): Promise<any> {
    try {
      const lookupFilter = { where: { MAC: mac }, limit: 1 };
      const lookupResponse: any = await this.devicesService.getDevices(lookupFilter)
        .pipe(take(1)).toPromise();

      let device: any = null;
      if (lookupResponse?.data?.length > 0) {
        device = lookupResponse.data[0];
      } else if (Array.isArray(lookupResponse) && lookupResponse.length > 0) {
        device = lookupResponse[0];
      }

      if (!device?.id) return null;

      const detailFilter = {
        where: { id: device.id },
        limit: 1,
        include: [
          { relation: 'tagEntitys' },
          { relation: 'deviceAlertConfig' },
          { relation: 'config' }
        ]
      };
      const detailResponse: any = await this.devicesService.getDevices(detailFilter)
        .pipe(take(1)).toPromise();

      if (detailResponse?.data?.length > 0) {
        return detailResponse.data[0];
      }
      if (Array.isArray(detailResponse) && detailResponse.length > 0) {
        return detailResponse[0];
      }

      return device;
    } catch {
      return null;
    }
  }

  private getDeviceIdentityContext(): string {
    if (!this.currentDeviceSummary) return '';
    const s = this.currentDeviceSummary;
    return `DEVICE IDENTITY:
- MAC: ${s.mac}
- Model: ${s.model}
- Firmware: ${s.firmware}
- IP: ${s.ip}
- Status: ${s.status}
- Network Mode: ${s.network}
- Band: ${s.band}`;
  }

  private formatDeviceData(data: any): string {
    const val = (v: any) => v !== undefined && v !== null && v !== '' && v !== -1000 && v !== 'Out of range';
    const uptime = (seconds: number): string => {
      if (!seconds) return 'N/A';
      const d = Math.floor(seconds / 86400);
      const h = Math.floor((seconds % 86400) / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      return `${d}d ${h}h ${m}m`;
    };

    const sections: string[] = [];

    let identitySection = `DEVICE IDENTITY:
- MAC Address: ${data.MAC}
- Model: ${data.MODEL}`;
    if (val(data.name)) identitySection += `\n- Name: ${data.name}`;
    if (val(data.HOST_NAME)) identitySection += `\n- Hostname: ${data.HOST_NAME}`;
    if (val(data.CustID)) identitySection += `\n- Customer ID: ${data.CustID}`;
    identitySection += `\n- Firmware: ${data.FW}`;
    if (val(data.radioType)) identitySection += `\n- Radio Type: ${data.radioType}`;
    if (val(data.IMEI)) identitySection += `\n- IMEI: ${data.IMEI}`;
    if (val(data.ICCID)) identitySection += `\n- ICCID: ${data.ICCID}`;
    identitySection += `\n- IP Address: ${data.IP}${val(data.IP2) ? ', ' + data.IP2 : ''}${val(data.IPV6) ? ', IPv6: ' + data.IPV6 : ''}`;
    sections.push(identitySection);

    const isOnline = data.Get_Sync === 'Yes' || data.connected === true;
    let connSection = `CONNECTION STATUS:
- Online/Sync: ${isOnline ? 'Online' : 'Offline'}
- Network Mode: ${data.NETMODE || 'N/A'}
- WAN Type: ${data.WAN}${val(data.WAN2) ? ', ' + data.WAN2 : ''}
- System Uptime: ${uptime(data.SYSUPTIME)}`;
    if (val(data.LTE_UPTIME)) connSection += `\n- LTE Uptime: ${uptime(data.LTE_UPTIME)}`;
    if (val(data.NETWORK)) connSection += `\n- Carrier: ${data.NETWORK}`;
    if (val(data.IMSI)) connSection += `\n- IMSI: ${data.IMSI}`;
    if (val(data.event)) connSection += `\n- Last Event: ${data.event}`;
    if (val(data.connectedAt)) connSection += `\n- Last Connected: ${new Date(data.connectedAt).toLocaleString()}`;
    if (val(data.disconnectedAt)) connSection += `\n- Disconnected At: ${new Date(data.disconnectedAt).toLocaleString()}`;
    if (val(data.disconnectedReason)) connSection += `\n- Disconnect Reason: ${data.disconnectedReason}`;
    sections.push(connSection);

    const rsrpRating = (v: number) => {
      if (v >= -80) return 'Excellent';
      if (v >= -90) return 'Good';
      if (v >= -100) return 'Fair';
      return 'Poor';
    };
    const sinrRating = (v: number) => {
      if (v >= 20) return 'Excellent';
      if (v >= 13) return 'Good';
      if (v >= 0) return 'Fair';
      return 'Poor';
    };
    const rsrqRating = (v: number) => {
      if (v >= -10) return 'Good';
      if (v >= -15) return 'Fair';
      return 'Poor';
    };

    if (val(data.RSRP)) {
      let lteSection = `LTE SIGNAL QUALITY:
- Band: ${data.BAND}
- Bandwidth: ${data.BW} MHz
- EARFCN (DL): ${data.RX_CH}
- Cell ID: ${data.CELL_ID}
- eNB ID: ${data.ENB_ID}
- ECI: ${data.ECI}
- PCI: ${data.PCI}
- RSSI: ${data.RSSI} dBm
- RSRP: ${data.RSRP} dBm (${rsrpRating(data.RSRP)})`;

      if (val(data.RSRP_DIV1)) lteSection += `\n- RSRP Diversity 1: ${data.RSRP_DIV1} dBm`;
      lteSection += `\n- RSRQ: ${data.RSRQ} dB (${rsrqRating(data.RSRQ)})`;
      lteSection += `\n- SINR: ${data.SINR} dB (${sinrRating(data.SINR)})`;
      lteSection += `\n- CQI: ${data.CQI}`;
      lteSection += `\n- Rank Indicator (RI): ${data.RI}`;
      lteSection += `\n- DL MCS: ${data.DL_MCS}`;
      lteSection += `\n- MIMO Mode: ${data.MIMO_MODE}`;
      sections.push(lteSection);
    }

    if (data.CA_STATUS && data.CA_STATUS !== 'INACTIVE') {
      let caSection = `CARRIER AGGREGATION:
- Status: ${data.CA_STATUS}
- Secondary Band: ${data.CA_SBAND}
- Secondary BW: ${data.CA_SBW} MHz
- Direction: ${data.CA_SDIR}
- Secondary Channel: ${data.CA_SCHAN}
- Secondary RSSI: ${data.CA_RSSI} dBm
- Secondary RSRP: ${data.CA_RSRP} dBm`;
      sections.push(caSection);
    } else {
      sections.push(`CARRIER AGGREGATION:\n- Status: Inactive`);
    }

    const hasNR = val(data.NR_RSRP) || val(data.NR_BAND);
    if (hasNR) {
      let nrSection = `5G NR STATUS:`;
      if (val(data.NR_BAND)) nrSection += `\n- NR Band: ${data.NR_BAND}`;
      if (val(data.NR_ARFCN)) nrSection += `\n- NR ARFCN: ${data.NR_ARFCN}`;
      if (val(data.NR_RSRP)) nrSection += `\n- NR RSRP: ${data.NR_RSRP} dBm`;
      if (val(data.NR_RSRQ)) nrSection += `\n- NR RSRQ: ${data.NR_RSRQ} dB`;
      if (val(data.NR_SINR)) nrSection += `\n- NR SINR: ${data.NR_SINR} dB`;
      if (val(data.NR_BW)) nrSection += `\n- NR BW: ${data.NR_BW} MHz`;
      sections.push(nrSection);
    } else {
      sections.push(`5G NR STATUS:\n- Not active (LTE only mode)`);
    }

    if (val(data.GTX) && val(data.GRX)) {
      const txMB = (data.GTX / 1024).toFixed(1);
      const rxMB = (data.GRX / 1024).toFixed(1);
      sections.push(`DATA USAGE:
- LTE TX: ${txMB} MB
- LTE RX: ${rxMB} MB`);
    }

    if (data.CBSD_STATUS && data.CBSD_STATUS !== 'Unregistered') {
      sections.push(`CBSD STATUS:
- Status: ${data.CBSD_STATUS}
- CBSD ID: ${data.CBSD_ID || 'N/A'}`);
    }

    if (val(data.GPS_LATITUDE) && val(data.GPS_LONGITUDE)) {
      sections.push(`GPS LOCATION:
- Latitude: ${data.GPS_LATITUDE}
- Longitude: ${data.GPS_LONGITUDE}`);
    }

    return sections.join('\n\n');
  }

  private parseSyslog(rawLog: string): string {
    const lines = rawLog.split('\n').filter(l => l.trim());
    const totalLines = lines.length;

    const errorKeywords = /\b(error|fail|critical|panic|fatal|exception)\b/i;
    const warnKeywords = /\b(warn|warning|timeout|refused|denied|reject)\b/i;
    const connectKeywords = /\b(disconnect|reconnect|restart|reboot|link down|link up|ppp|dhcp|attach|detach)\b/i;

    const errorLines: string[] = [];
    const warnLines: string[] = [];
    const connectLines: string[] = [];

    for (const line of lines) {
      if (errorKeywords.test(line)) {
        errorLines.push(line.trim());
      } else if (warnKeywords.test(line)) {
        warnLines.push(line.trim());
      } else if (connectKeywords.test(line)) {
        connectLines.push(line.trim());
      }
    }

    const firstLine = lines[0]?.trim() || 'N/A';
    const lastLine = lines[lines.length - 1]?.trim() || 'N/A';

    const sections: string[] = [];

    sections.push(`SYSTEM LOG SUMMARY:
- Total log lines: ${totalLines}
- Error entries: ${errorLines.length}
- Warning entries: ${warnLines.length}
- Connectivity events: ${connectLines.length}
- Log start: ${firstLine.substring(0, 80)}
- Log end: ${lastLine.substring(0, 80)}`);

    if (errorLines.length > 0) {
      const recentErrors = errorLines.slice(-15);
      sections.push(`RECENT ERRORS (last ${recentErrors.length} of ${errorLines.length}):\n${recentErrors.map(l => '- ' + l.substring(0, 200)).join('\n')}`);
    }

    if (warnLines.length > 0) {
      const recentWarnings = warnLines.slice(-10);
      sections.push(`RECENT WARNINGS (last ${recentWarnings.length} of ${warnLines.length}):\n${recentWarnings.map(l => '- ' + l.substring(0, 200)).join('\n')}`);
    }

    if (connectLines.length > 0) {
      const recentConnect = connectLines.slice(-10);
      sections.push(`CONNECTIVITY EVENTS (last ${recentConnect.length} of ${connectLines.length}):\n${recentConnect.map(l => '- ' + l.substring(0, 200)).join('\n')}`);
    }

    if (errorLines.length === 0 && warnLines.length === 0 && connectLines.length === 0) {
      const lastLines = lines.slice(-10);
      sections.push(`LAST 10 LOG ENTRIES:\n${lastLines.map(l => '- ' + l.substring(0, 200)).join('\n')}`);
    }

    return sections.join('\n\n');
  }

  async fetchAndAnalyzeSyslog(connectivityMode = false, pingResults = '') {
    if (this.isLoading || this.isFetchingDevice || !this.currentDeviceMAC) return;

    if (!this.isDeviceOnline) {
      this.messages.push({
        role: 'assistant',
        content: this._translate.instant('CHATBOT.SYSLOG_DEVICE_OFFLINE')
      });
      this.cdr.detectChanges();
      this.scrollToBottom();
      return;
    }

    this.isFetchingDevice = true;
    this.setStatus(2, this._translate.instant('CHATBOT.STATUS_FETCHING_SYSLOG'));

    try {
      const response = await this.deviceCmd.sendDeviceCommand({
        MAC: this.currentDeviceMAC,
        command: 'GET_SYSLOG' as any
      }).pipe(take(1)).toPromise();

      if (!response?.isSuccessful || !response?.data) {
        this.clearStatus();
        this.messages.push({
          role: 'assistant',
          content: this._translate.instant('CHATBOT.SYSLOG_FETCH_ERROR')
        });
        this.isFetchingDevice = false;
        this.cdr.detectChanges();
        return;
      }

      const rawLog = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      this.currentSyslog = rawLog;
      this.currentSyslogSummary = this.parseSyslog(rawLog);

      this.setStatus(3, this._translate.instant('CHATBOT.STATUS_ANALYZING_SYSLOG'));
      this.isFetchingDevice = false;
      this.isLoading = true;
      this.cdr.detectChanges();

      this.messages.push({ role: 'assistant', content: '' });
      this.cdr.detectChanges();

      let aiPrompt: string;
      if (connectivityMode) {
        const pingSection = pingResults ? `\n\n${pingResults}` : '';
        aiPrompt = `I need help troubleshooting connectivity for this device. Here is the full device data, system log, and ping test results:\n\n${this.currentDeviceContext}\n\n${this.currentSyslogSummary}${pingSection}\n\nAnalyze the device telemetry, system log, and ping results together to diagnose connectivity issues. Look for signal problems, connection drops, errors, latency issues, and patterns that explain connectivity behavior. Provide troubleshooting recommendations.`;
      } else {
        aiPrompt = `I need help analyzing this device's system log. Here is the device identity and log summary:\n\n${this.getDeviceIdentityContext()}\n\n${this.currentSyslogSummary}\n\nFocus your analysis exclusively on the system log content. Look for errors, warnings, connectivity events, recurring patterns, and anomalies in the log entries. Do NOT analyze signal quality, LTE metrics, or device configuration — only the system log. Provide actionable recommendations based on the log findings.`;
      }

      const aiMessages = [
        {
          role: 'user',
          content: aiPrompt
        }
      ];

      const controller = new AbortController();
      this.currentAbortController = controller;

      let isTimedOut = false;
      const timeoutHandle = setTimeout(() => { isTimedOut = true; controller.abort(); }, 90000);

      try {
        const fetchResponse = await fetch('/api/chat', {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ messages: aiMessages }),
          signal: controller.signal
        });

        if (fetchResponse.status === 401 || fetchResponse.status === 402) { if (await this._handleBillingLimit(fetchResponse)) return; }
        if (!fetchResponse.ok) throw new Error('Failed to get AI response');

        this.clearStatus();
        const assistantMessage = this.messages[this.messages.length - 1];
        const reader = fetchResponse.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          assistantMessage.content += decoder.decode(value, { stream: true });
          this.cdr.detectChanges();
          this.scrollToBottom();
        }

        if (assistantMessage.content && assistantMessage.content.length > 50) {
          this.lastAIDiagnostic = assistantMessage.content;
        }
      } catch (err) {
        if (err?.name === 'AbortError') {
          const lastMsg = this.messages[this.messages.length - 1];
          if (lastMsg && !lastMsg.content) lastMsg.content = isTimedOut ? this._translate.instant('CHATBOT.ERROR_TIMED_OUT') : this._translate.instant('CHATBOT.ERROR_RESPONSE_STOPPED');
        } else {
          const lastMsg = this.messages[this.messages.length - 1];
          if (!lastMsg?.content) {
            lastMsg.content = this._translate.instant('CHATBOT.ERROR_SYSLOG_FAILED');
          }
        }
        this.cdr.detectChanges();
      } finally {
        clearTimeout(timeoutHandle);
      }

    } catch (err: any) {
      const lastMsg = this.messages[this.messages.length - 1];
      if (lastMsg && !lastMsg.content) lastMsg.content = this._translate.instant('CHATBOT.ERROR_SYSLOG_RETRIEVE');
      this.cdr.detectChanges();
    }

    this.currentAbortController = null;
    this.isFetchingDevice = false;
    this.isLoading = false;
    this.clearStatus();
    this.cdr.detectChanges();
    this.scrollToBottom();
    setTimeout(() => this.messageInput?.nativeElement?.focus(), 100);
  }

  async fetchDeviceAlerts(mac: string) {
    this.isFetchingDevice = true;
    this.setStatus(2, this._translate.instant('CHATBOT.STATUS_FETCHING_ALERTS'));

    try {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const alertFilter = {
        where: {
          and: [
            { timestamp: { gte: sevenDaysAgo.toISOString() } },
            { timestamp: { lte: now.toISOString() } },
            {
              or: [
                { MAC: { like: `.*${mac}.*`, options: 'i' } },
                { deviceName: { like: `.*${mac}.*`, options: 'i' } },
                { HOST_NAME: { like: `.*${mac}.*`, options: 'i' } }
              ]
            }
          ]
        },
        order: 'timestamp DESC',
        limit: 100,
        skip: 0,
        include: [
          {
            relation: 'device',
            scope: {
              fields: ['id', 'name', 'IMSI', 'FW', 'RI', 'DL_MCS', 'HOST_NAME']
            }
          }
        ]
      };

      const response: any = await this.deviceAlarmService.getDeviceAlaram(alertFilter)
        .pipe(take(1)).toPromise();

      let alerts: any[] = [];
      let alertsFetchFailed = false;
      if (response?.isSuccessful && Array.isArray(response?.data)) {
        alerts = response.data;
      } else if (Array.isArray(response)) {
        alerts = response;
      } else {
        alertsFetchFailed = true;
      }

      if (alertsFetchFailed) {
        this.clearStatus();
        this.messages.push({
          role: 'assistant',
          content: this._translate.instant('CHATBOT.ALERTS_FETCH_ERROR')
        });
        this.isFetchingDevice = false;
        this.isLoading = false;
        this.cdr.detectChanges();
        this.scrollToBottom();
        return;
      }

      this.setStatus(3, this._translate.instant('CHATBOT.STATUS_ANALYZING_ALERTS'));
      this.isFetchingDevice = false;
      this.isLoading = true;
      this.cdr.detectChanges();

      const alertsSummary = this.formatAlerts(alerts);

      this.messages.push({ role: 'assistant', content: '' });
      this.cdr.detectChanges();

      const aiMessages = [
        {
          role: 'user',
          content: `I need help investigating alerts for this device. Here is the device identity and recent alerts:\n\n${this.getDeviceIdentityContext()}\n\n${alertsSummary}\n\nFocus your analysis exclusively on the alert data. Identify alert patterns, frequency, severity levels, and correlations between alert types. Do NOT analyze signal quality, LTE metrics, or device configuration — only the alerts. Provide actionable recommendations based on the alert findings.`
        }
      ];

      const controller = new AbortController();
      this.currentAbortController = controller;

      let isTimedOut = false;
      const timeoutHandle = setTimeout(() => { isTimedOut = true; controller.abort(); }, 90000);

      try {
        const fetchResponse = await fetch('/api/chat', {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ messages: aiMessages }),
          signal: controller.signal
        });

        if (fetchResponse.status === 401 || fetchResponse.status === 402) { if (await this._handleBillingLimit(fetchResponse)) return; }
        if (!fetchResponse.ok) throw new Error('Failed to get AI response');

        this.clearStatus();
        const assistantMessage = this.messages[this.messages.length - 1];
        const reader = fetchResponse.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          assistantMessage.content += decoder.decode(value, { stream: true });
          this.cdr.detectChanges();
          this.scrollToBottom();
        }

        if (assistantMessage.content && assistantMessage.content.length > 50) {
          this.lastAIDiagnostic = assistantMessage.content;
        }
      } catch (err) {
        if (err?.name === 'AbortError') {
          const lastMsg = this.messages[this.messages.length - 1];
          if (lastMsg && !lastMsg.content) lastMsg.content = isTimedOut ? this._translate.instant('CHATBOT.ERROR_TIMED_OUT') : this._translate.instant('CHATBOT.ERROR_RESPONSE_STOPPED');
        } else {
          this.messages.push({
            role: 'assistant',
            content: this._translate.instant('CHATBOT.ALERTS_ANALYZE_ERROR')
          });
        }
        this.cdr.detectChanges();
      } finally {
        clearTimeout(timeoutHandle);
      }

    } catch (err: any) {
      const lastMsg = this.messages[this.messages.length - 1];
      if (lastMsg && !lastMsg.content) lastMsg.content = this._translate.instant('CHATBOT.ALERTS_ANALYZE_ERROR');
      this.cdr.detectChanges();
    }

    this.currentAbortController = null;
    this.isFetchingDevice = false;
    this.isLoading = false;
    this.clearStatus();
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private formatAlerts(alerts: any[]): string {
    if (!alerts || alerts.length === 0) {
      return 'DEVICE ALERTS (Last 7 Days):\n- No alerts found for this device in the last 7 days.';
    }

    const typeCounts: Record<string, number> = {};
    for (const a of alerts) {
      typeCounts[a.type || 'unknown'] = (typeCounts[a.type || 'unknown'] || 0) + 1;
    }

    let summary = `DEVICE ALERTS (Last 7 Days):\n- Total alerts: ${alerts.length}\n`;
    summary += `- Alert types: ${Object.entries(typeCounts).map(([t, c]) => `${t} (${c})`).join(', ')}\n\n`;

    summary += 'RECENT ALERTS:\n';
    const recentAlerts = alerts.slice(0, 25);
    for (const a of recentAlerts) {
      const ts = a.timestamp ? new Date(a.timestamp).toLocaleString() : 'N/A';
      summary += `- [${ts}] ${a.level || 'Info'} | ${a.type || 'unknown'}: ${a.value || 'No details'}\n`;
    }

    if (alerts.length > 25) {
      summary += `\n... and ${alerts.length - 25} more alerts`;
    }

    return summary;
  }

  submitSupportTicket() {
    if (!this.currentDeviceSummary || !this.currentDeviceMAC) return;

    const s = this.currentDeviceSummary;
    const subject = `BECentral Support - Device ${s.mac} - ${s.model}`;

    let body = `SUPPORT TICKET\n${'='.repeat(40)}\n\n`;

    body += `DEVICE INFORMATION:\n`;
    body += `- MAC Address: ${s.mac}\n`;
    body += `- Model: ${s.model}\n`;
    body += `- Firmware: ${s.firmware}\n`;
    body += `- IP Address: ${s.ip}\n`;
    body += `- Status: ${s.status}\n`;
    body += `- Network: ${s.network} / ${s.band}\n`;
    if (s.rsrp !== null) body += `- RSRP: ${s.rsrp} dBm\n`;
    if (s.sinr !== null) body += `- SINR: ${s.sinr} dB\n`;
    body += `\n`;

    if (this.currentSyslogSummary) {
      const syslogTruncated = this.currentSyslogSummary.length > 1000
        ? this.currentSyslogSummary.substring(0, 1000) + '...'
        : this.currentSyslogSummary;
      body += `SYSTEM LOG SUMMARY:\n`;
      body += syslogTruncated + '\n\n';
    }

    if (this.lastAIDiagnostic) {
      const truncated = this.lastAIDiagnostic.length > 1500
        ? this.lastAIDiagnostic.substring(0, 1500) + '...'
        : this.lastAIDiagnostic;
      body += `AI DIAGNOSTIC SUMMARY:\n${truncated}\n\n`;
    }

    body += `ISSUE DESCRIPTION:\n[Please describe the issue you are experiencing]\n\n`;
    body += `---\nGenerated by BECentral Copilot`;

    const mailtoUrl = `mailto:helpdesk@becentral.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');

    this.messages.push({
      role: 'assistant',
      content: this._translate.instant('CHATBOT.TICKET_OPENED')
    });
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  stopGeneration() {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
    this.isLoading = false;
    this.clearStatus();
    this.cdr.detectChanges();
  }

  private async streamAIAnalysis() {
    const assistantMessage = this.messages[this.messages.length - 1];

    try {
      const aiMessages = [
        {
          role: 'user',
          content: `I need help diagnosing this 4G/5G device. Here is the device data:\n\n${this.currentDeviceContext}\n\nPlease analyze this device's status and signal quality. Identify any issues and provide recommendations.`
        }
      ];

      const controller = new AbortController();
      this.currentAbortController = controller;

      let isTimedOut = false;
      const timeoutHandle = setTimeout(() => { isTimedOut = true; controller.abort(); }, 90000);

      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ messages: aiMessages }),
          signal: controller.signal
        });

        if (response.status === 401 || response.status === 402) { if (await this._handleBillingLimit(response)) return; }
        if (!response.ok) throw new Error('Failed to get AI response');

        this.clearStatus();
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          assistantMessage.content += decoder.decode(value, { stream: true });
          this.cdr.detectChanges();
          this.scrollToBottom();
        }
      } catch (err) {
        if (err?.name === 'AbortError') {
          if (!assistantMessage.content) assistantMessage.content = isTimedOut ? this._translate.instant('CHATBOT.ERROR_TIMED_OUT') : this._translate.instant('CHATBOT.ERROR_RESPONSE_STOPPED');
        } else {
          assistantMessage.content = this._translate.instant('CHATBOT.ERROR_DEVICE_FAILED');
        }
        this.cdr.detectChanges();
      } finally {
        clearTimeout(timeoutHandle);
      }

    } catch (err: any) {
      if (!assistantMessage.content) assistantMessage.content = this._translate.instant('CHATBOT.ERROR_DEVICE_FAILED');
      this.cdr.detectChanges();
    }

    if (assistantMessage.content && assistantMessage.content.length > 50) {
      this.lastAIDiagnostic = assistantMessage.content;
    }

    this.currentAbortController = null;
    this.isLoading = false;
    this.clearStatus();
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private getAuthHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'x-lang': this.coreTranslating.currentLang
    };
  }

  private startLoadingMessages() {
    this.stopLoadingMessages();
    this.loadingStatusText = this._translate.instant('CHATBOT.LOADING_ANALYZING');
    this.cdr.detectChanges();

    const messages = [
      { delay: 6000, text: this._translate.instant('CHATBOT.LOADING_RETRIEVING') },
      { delay: 13000, text: this._translate.instant('CHATBOT.LOADING_PROCESSING') },
      { delay: 22000, text: this._translate.instant('CHATBOT.LOADING_EVALUATING') },
      { delay: 35000, text: this._translate.instant('CHATBOT.LOADING_DEEPER') },
      { delay: 50000, text: this._translate.instant('CHATBOT.LOADING_FINALIZING') },
    ];

    for (const msg of messages) {
      const timer = setTimeout(() => {
        if (this.isLoading) {
          this.loadingStatusText = msg.text;
          this.cdr.detectChanges();
        }
      }, msg.delay);
      this.loadingStatusTimers.push(timer);
    }
  }

  private stopLoadingMessages() {
    for (const timer of this.loadingStatusTimers) {
      clearTimeout(timer);
    }
    this.loadingStatusTimers = [];
    this.loadingStatusText = '';
  }

  private async sendToAI(useTools = false) {
    this.isLoading = true;
    this.startLoadingMessages();
    this.cdr.detectChanges();

    const assistantMessage: ChatMessage = { role: 'assistant', content: '' };
    this.messages.push(assistantMessage);
    this.scrollToBottom();

    let isTimedOut = false;
    let timeoutHandle: any = null;

    try {
      const aiMessages = [];

      if (this.currentDeviceContext) {
        let contextMsg = `[Device Context - MAC: ${this.currentDeviceMAC}]\n${this.currentDeviceContext}`;
        if (this.currentSyslogSummary) {
          contextMsg += `\n\n${this.currentSyslogSummary}`;
        }
        aiMessages.push({
          role: 'user',
          content: contextMsg
        });
        aiMessages.push({
          role: 'assistant',
          content: 'I have the device data loaded. What would you like to know?'
        });
      }

      const conversationMessages = this.messages
        .slice(0, -1)
        .filter(m => (m.role === 'user' || m.role === 'assistant') && m.content && !m.isDeviceCard && !m.isStatusMessage)
        .map(m => ({ role: m.role, content: m.content }));

      aiMessages.push(...conversationMessages);

      const controller = new AbortController();
      this.currentAbortController = controller;

      timeoutHandle = setTimeout(() => {
        isTimedOut = true;
        controller.abort();
      }, 60000);

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ messages: aiMessages, useTools }),
        signal: controller.signal
      });

      if (response.status === 401 || response.status === 402) { if (await this._handleBillingLimit(response)) return; }
      if (!response.ok) throw new Error('Failed to get response');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (chunk && !assistantMessage.content) {
          this.stopLoadingMessages();
        }
        assistantMessage.content += chunk;
        this.cdr.detectChanges();
        this.scrollToBottom();
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        if (isTimedOut) {
          if (!assistantMessage.content) {
            assistantMessage.content = this._translate.instant('CHATBOT.ERROR_TIMED_OUT_SCHEDULED');
          }
        } else {
          if (!assistantMessage.content) assistantMessage.content = this._translate.instant('CHATBOT.ERROR_RESPONSE_STOPPED');
        }
      } else {
        assistantMessage.content = this._translate.instant('CHATBOT.ERROR_GENERAL');
      }
      this.cdr.detectChanges();
    }

    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }

    this.stopLoadingMessages();

    if (!assistantMessage.content) {
      assistantMessage.content = this._translate.instant('CHATBOT.ERROR_NO_RESPONSE');
      this.cdr.detectChanges();
    }

    if (assistantMessage.content && assistantMessage.content.length > 50) {
      this.lastAIDiagnostic = assistantMessage.content;
    }

    this.currentAbortController = null;
    this.isLoading = false;
    this.cdr.detectChanges();
    this.scrollToBottom();

    // Auto-populate device context only when AI response is about a single specific device
    if (!this.currentDeviceContext && assistantMessage.content) {
      const allMACs = this.extractAllMACsFromText(assistantMessage.content);
      if (allMACs.length === 1) {
        // Exactly one MAC — single-device response, safe to auto-populate
        await this.fetchDeviceInfo(allMACs[0]);
      } else if (allMACs.length === 0) {
        // No MAC in response — try detecting a device name from the user's message
        const userMessages = this.messages.filter(m => m.role === 'user');
        const lastUserMsg = userMessages.length > 0 ? userMessages[userMessages.length - 1].content : '';
        const detectedName = this.extractDeviceNameFromText(lastUserMsg);
        if (detectedName) {
          await this.fetchDeviceInfoByName(detectedName);
        }
      }
      // If allMACs.length > 1, this is a fleet response — do not render a device card
    }

    setTimeout(() => this.messageInput?.nativeElement?.focus(), 100);
  }

  private async _handleBillingLimit(response: Response): Promise<boolean> {
    if (response.status === 401) {
      const msg = this._translate.instant('CHATBOT.ERROR_SESSION_EXPIRED');
      const lastMsg = this.messages[this.messages.length - 1];
      if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.content) {
        lastMsg.content = msg;
        lastMsg.isAuthError = true;
      } else {
        this.messages.push({ role: 'assistant', content: msg, isAuthError: true });
      }
      this.cdr.detectChanges();
      return true;
    }
    if (response.status !== 402) return false;
    let reason = 'Your usage limit has been reached.';
    try {
      const data = await response.json();
      if (data?.error) reason = data.error;
    } catch {}
    this.billingLimitMessage = reason;
    const lastMsg = this.messages[this.messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.content) {
      lastMsg.content = reason;
      lastMsg.isBillingLimit = true;
    } else {
      this.messages.push({ role: 'assistant', content: reason, isBillingLimit: true });
    }
    this.cdr.detectChanges();
    return true;
  }

  async sendQuickAction(action: string) {
    if (this.isLoading || this.isFetchingDevice) return;
    this.inputText = action;
    await this.sendMessage();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearChat() {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
    this.stopLoadingMessages();
    this.messages = [];
    this.currentDeviceContext = '';
    this.currentDeviceMAC = '';
    this.currentDeviceSummary = null;
    this.currentSyslog = '';
    this.currentSyslogSummary = '';
    this.lastAIDiagnostic = '';
    this.pendingWorkflow = null;
    this.usedFallbackData = false;
    this.isLoading = false;
    this.isFetchingDevice = false;
    this.clearStatus();
  }

  getSignalClass(value: number, type: string): string {
    if (type === 'rsrp') {
      if (value >= -80) return 'signal-excellent';
      if (value >= -90) return 'signal-good';
      if (value >= -100) return 'signal-fair';
      return 'signal-poor';
    }
    if (type === 'sinr') {
      if (value >= 20) return 'signal-excellent';
      if (value >= 13) return 'signal-good';
      if (value >= 0) return 'signal-fair';
      return 'signal-poor';
    }
    return '';
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }
}
